import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import ToastHost, { useToast } from '../shared/Toast';

// Local copy of the currency-symbol helper (not exported from a shared location).
const CC = ({ size = '1em' }) => (
  <span style={{ fontSize: size, marginLeft: 4, verticalAlign: 'baseline' }}>₵₵</span>
);

const AGENT_GENERAL_PERMS = [
  ['can_update_info', 'Update Info', 'Change name, username, and password'],
  ['can_add_customers', 'Add Customers', 'Create new customer accounts'],
  ['can_add_agents', 'Add Agents', 'Create new agent (sub-agent) accounts'],
  ['can_suspend_wagering', 'Suspend Wagering', "Enable read-only mode on a customer's account"],
  ['can_delete_transactions', 'Delete Transactions', "Void a customer's bets"],
  ['can_deactivate_account', 'Deactivate Account', "Fully disable a customer's login."],
  ['can_change_settle_figure', 'Adjust Balance', "Directly edit a customer's balance."],
  ['can_adjust_free_play_balance', 'Adjust Free Play Balance', "Directly credit or debit a customer's free play balance."],
];

const AGENT_SITE_OPTIONS = [
  ['can_allow_deny_casino', 'Allow/Deny Casino', "Change a customer's casino access"],
  ['can_view_ip', 'IP Access', "See a customer's IP address"],
  ['can_access_game_admin', 'Game Admin Access', "Let this agent narrow which of your enabled sports their customers can bet on"],
  ['can_access_feedback', 'Feedback Access', 'Give feedback'],
  ['can_access_settings', 'Cashout Control', "Disable cashout for a specific customer."],
];

// [key, toggleField, rangeMinField, rangeMaxField, label, defaultFields, placeholders]
// defaultFields/placeholders are a 1-tuple for a single-value category or a
// [min, max] pair for a category whose customer-side field is itself a
// min/max pair. placeholders are the site's standard starting values — shown
// as greyed-out hint text in an unconfigured Default box (still null/unset
// underneath) so the box shows what's actually happening today instead of a
// blank dash, without silently writing anything until the book types a value.
const AGENT_LIMIT_TYPES = [
  ['credit', 'can_change_credit_limit', 'credit_limit_min', 'credit_limit_max', 'Change Credit Limit', ['credit_default'], [10000]],
  ['free_play', 'can_change_free_play_limit', 'free_play_limit_min', 'free_play_limit_max', 'Change Free Play Limit (%)', ['free_play_default'], [0]],
  ['wager', 'can_change_wager_limit', 'wager_limit_min', 'wager_limit_max', 'Change Wager Limit', ['wager_default_min', 'wager_default_max'], [0.01, 1000]],
  ['live_wager', 'can_change_live_wager_limit', 'live_wager_limit_min', 'live_wager_limit_max', 'Change Live Wager Limit', ['live_wager_default_min', 'live_wager_default_max'], [10, 1000]],
  ['parlay', 'can_change_parlay_limit', 'parlay_limit_min', 'parlay_limit_max', 'Change Parlay Limit', ['parlay_default_min', 'parlay_default_max'], [0.01, 500]],
  ['contest', 'can_change_contest_limit', 'contest_limit_min', 'contest_limit_max', 'Change Contest Limit', ['contest_default'], [0]],
  ['casino', 'can_change_casino_limit', 'casino_limit_min', 'casino_limit_max', 'Change Casino Limit', ['casino_default_min', 'casino_default_max'], [10, 1000]],
  ['casino_weekly', 'can_change_casino_weekly_limit', 'casino_weekly_limit_min', 'casino_weekly_limit_max', 'Change Casino Weekly Win/Loss Limit', ['casino_weekly_win_default', 'casino_weekly_loss_default'], [0, 0]],
];

export default function UserDetailModal({ user: selectedUser, onClose, onUpdate, currentUser, accounts }) {
  const showAgentPerms = selectedUser.role === 'agent' && (currentUser.role === 'book' || currentUser.role === 'admin');

  const [editData, setEditData] = useState({
    name: selectedUser.name,
    username: selectedUser.username,
    new_password: '',
    weekly_limit: selectedUser.weekly_limit,
    min_bet: selectedUser.min_bet,
    max_bet: selectedUser.max_bet,
    parlay_min_bet: selectedUser.parlay_min_bet || 0.01,
    parlay_max_bet: selectedUser.parlay_max_bet || 500,
    live_min_bet: selectedUser.live_min_bet ?? 10,
    live_max_bet: selectedUser.live_max_bet ?? 1000,
    casino_min_bet: selectedUser.casino_min_bet ?? 10,
    casino_max_bet: selectedUser.casino_max_bet ?? 1000,
    casino_weekly_max_win: selectedUser.casino_weekly_max_win ?? 0,
    casino_weekly_max_loss: selectedUser.casino_weekly_max_loss ?? 0,
    parlay_max_payout: selectedUser.parlay_max_payout || 0,
    is_active: selectedUser.is_active,
    is_read_only: selectedUser.is_read_only || false,
    agent_cut: selectedUser.agent_cut || 50,
    free_play_loss_percentage: selectedUser.free_play_loss_percentage || 0,
    game_limit: selectedUser.game_limit || 0,
    sportsbook_access: selectedUser.sportsbook_access !== false,
    casino_access: selectedUser.casino_access || false,
    cashout_disabled_for_customer: selectedUser.cashout_disabled_for_customer || false,
  });
  const [balanceAdjustment, setBalanceAdjustment] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [freePlayAdjustment, setFreePlayAdjustment] = useState('');
  const [freePlayReason, setFreePlayReason] = useState('');
  const [pendingBets, setPendingBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(false);
  const [voidingBetId, setVoidingBetId] = useState(null);
  const [openSections, setOpenSections] = useState(new Set(['general']));
  const [saving, setSaving] = useState(false);

  const [perms, setPerms] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(showAgentPerms);
  const [permsError, setPermsError] = useState(null);
  const { toast, showToast } = useToast();

  const toggleSection = (key) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  useEffect(() => {
    if (selectedUser.role !== 'customer') return;
    setLoadingBets(true);
    api.getBetHistory(selectedUser.id, 'pending', 'all')
      .then((res) => {
        const singles = (res.single_bets || []).map((b) => ({ ...b, is_parlay: false }));
        const parlays = (res.parlays || []).map((p) => ({ ...p, is_parlay: true }));
        setPendingBets([...singles, ...parlays]);
      })
      .catch(() => setPendingBets([]))
      .finally(() => setLoadingBets(false));
  }, [selectedUser.id, selectedUser.role]);

  useEffect(() => {
    if (!showAgentPerms) return;
    setLoadingPerms(true);
    setPermsError(null);
    api.getAgentPermissions(selectedUser.id)
      .then(data => setPerms(data))
      .catch(err => setPermsError(err.response?.data?.error || 'Failed to load agent permissions'))
      .finally(() => setLoadingPerms(false));
  }, [selectedUser.id, showAgentPerms]);

  const setPermBool = (field, value) => setPerms(prev => ({ ...prev, [field]: value }));
  const setPermNum = (field, value) => setPerms(prev => ({ ...prev, [field]: Number(value) }));
  const setPermDefault = (field, value) => setPerms(prev => ({ ...prev, [field]: value === '' ? null : Number(value) }));

  const handleVoidBet = async (bet) => {
    if (!window.confirm('Void this bet? This cannot be undone.')) return;
    const rowKey = `${bet.is_parlay ? 'p' : 'b'}-${bet.id}`;
    setVoidingBetId(rowKey);
    try {
      if (bet.is_parlay) {
        await api.cancelParlay(bet.id);
      } else {
        await api.cancelBet(bet.id);
      }
      setPendingBets((prev) => prev.filter((b) => !(b.is_parlay === bet.is_parlay && b.id === bet.id)));
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to void bet', 'error');
    } finally {
      setVoidingBetId(null);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const updateData = { ...editData };
      if (!updateData.new_password) {
        delete updateData.new_password;
      }
      if (balanceAdjustment && parseFloat(balanceAdjustment) !== 0) {
        updateData.balance_adjustment = parseFloat(balanceAdjustment);
        if (adjustmentReason) {
          updateData.adjustment_reason = adjustmentReason;
        }
      }
      if (freePlayAdjustment && parseFloat(freePlayAdjustment) !== 0) {
        updateData.free_play_adjustment = parseFloat(freePlayAdjustment);
        if (freePlayReason) {
          updateData.free_play_reason = freePlayReason;
        }
      }
      const calls = [api.updateAccount(selectedUser.id, updateData)];
      if (showAgentPerms && perms) {
        calls.push(api.updateAgentPermissions(selectedUser.id, perms));
      }
      await Promise.all(calls);
      onUpdate();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update account', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Calculate minimum allowed cut based on allocated cuts to sub-agents
  const getMinimumAllowedCut = () => {
    if (selectedUser.role !== 'agent') return 0;

    // Get all direct agent children's cuts
    const childAgents = accounts.filter(acc =>
      acc.created_by === selectedUser.id && acc.role === 'agent'
    );

    const totalAllocated = childAgents.reduce((sum, agent) => sum + (agent.agent_cut || 0), 0);
    return totalAllocated;
  };

  const minimumCut = getMinimumAllowedCut();

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' };
  const modalStyle = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, maxWidth: '56rem', width: '100%', maxHeight: '90vh', overflowY: 'auto', fontFamily };
  const sectionCard = { background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, marginBottom: '1rem' };
  const sectionHeader = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.textPrimary, fontFamily, fontWeight: 600, fontSize: '1rem' };
  const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };
  const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };
  const hintStyle = { color: colors.textFaint, fontSize: '0.75rem', marginTop: '0.3rem' };
  const saveButtonStyle = { marginTop: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };
  const limitRowStyle = { padding: '0.6rem 0', borderBottom: `1px solid ${colors.sectionBg}` };
  const limitInputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.3rem 0.5rem', width: 90, boxSizing: 'border-box', fontSize: '0.8rem', fontFamily };
  const numInputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.3rem 0.5rem', width: 90, boxSizing: 'border-box', fontSize: '0.8rem', fontFamily };
  const checkboxRow = { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.5rem 0', borderBottom: `1px solid ${colors.sectionBg}` };
  const limitRow = (label, input, hint) => (
    <div style={limitRowStyle}>
      <div style={{ marginBottom: '0.4rem' }}>
        <span style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
        {hint && <p style={{ ...hintStyle, marginTop: 2 }}>{hint}</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: '1.5rem' }}>{input}</div>
    </div>
  );
  const pairRow = (label, minInput, maxInput, hint) => (
    <div style={limitRowStyle}>
      <div style={{ marginBottom: '0.4rem' }}>
        <span style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
        {hint && <p style={{ ...hintStyle, marginTop: 2 }}>{hint}</p>}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.5rem' }}>
        <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>Min {minInput}</label>
        <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>Max {maxInput}</label>
      </div>
    </div>
  );
  const permCheckbox = (field, label, hint) => (
    <label key={field} style={{ ...checkboxRow, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!perms[field]} onChange={e => setPermBool(field, e.target.checked)} style={{ marginTop: 3, accentColor: colors.gold, cursor: 'pointer' }} />
      <div>
        <div style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</div>
        <div style={{ color: colors.textMuted, fontSize: '0.78rem' }}>{hint}</div>
      </div>
    </label>
  );

  return (
    <div style={overlayStyle}>
      <ToastHost toast={toast} />
      <div className="scrollbar-hide" style={modalStyle}>
        <div style={{ padding: '1.25rem', borderBottom: `1px solid ${colors.goldMuted}`, position: 'sticky', top: 0, background: colors.bgGradient }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ color: colors.textPrimary, margin: 0 }}>{selectedUser.name}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textPrimary, fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
          </div>
          <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.3rem' }}>
            <p style={{ margin: 0 }}>Username: <span style={{ fontWeight: 600, color: colors.textPrimary }}>{selectedUser.username}</span> | Role: <span style={{ fontWeight: 600, color: colors.textPrimary }}>{selectedUser.role}</span></p>
            {selectedUser.password_plain && (
              <p style={{ margin: '0.3rem 0 0' }}>Password: <span style={{ fontFamily: 'monospace', background: colors.cardBg, color: colors.textPrimary, padding: '2px 6px', borderRadius: 4 }}>{selectedUser.password_plain}</span></p>
            )}
          </div>
        </div>

        <div style={{ padding: '1.25rem' }}>
          <div style={sectionCard}>
            <button onClick={() => toggleSection('general')} style={sectionHeader}>
              <span>General Account Setup</span>
              {openSections.has('general') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
            </button>
            {openSections.has('general') && (
              <div style={{ padding: '0 1.25rem 1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Name</label>
                    <input style={inputStyle} value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>Username</label>
                    <input style={inputStyle} value={editData.username} onChange={(e) => setEditData({ ...editData, username: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>New Password</label>
                    <input style={inputStyle} placeholder="Leave blank to keep current password" value={editData.new_password} onChange={(e) => setEditData({ ...editData, new_password: e.target.value })} />
                  </div>
                  {selectedUser.role === 'agent' && (
                    <div>
                      <label style={labelStyle}>Agent Cut (%)</label>
                      <input
                        type="number"
                        min={minimumCut}
                        max="100"
                        step="0.1"
                        style={inputStyle}
                        value={editData.agent_cut}
                        onChange={(e) => setEditData({ ...editData, agent_cut: Number(e.target.value) })}
                      />
                    </div>
                  )}
                  {selectedUser.role === 'customer' && (
                    <div>
                      <label style={labelStyle}>Product Access</label>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', paddingTop: '0.4rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editData.sportsbook_access} onChange={(e) => setEditData({ ...editData, sportsbook_access: e.target.checked })} />
                          Sportsbook
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editData.casino_access} onChange={(e) => setEditData({ ...editData, casino_access: e.target.checked })} />
                          Casino
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '1.25rem' }}>
                  {selectedUser.role === 'customer' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input
                        type="checkbox"
                        id="cashout_disabled_for_customer"
                        checked={editData.cashout_disabled_for_customer}
                        onChange={(e) => setEditData({ ...editData, cashout_disabled_for_customer: e.target.checked })}
                      />
                      <label htmlFor="cashout_disabled_for_customer" style={{ color: colors.textPrimary, fontSize: '0.9rem' }}>Disable Cashout for This Customer</label>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={editData.is_active}
                      onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                    />
                    <label htmlFor="is_active" style={{ color: colors.textPrimary, fontSize: '0.9rem' }}>Account Active</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="checkbox"
                      id="is_read_only"
                      checked={editData.is_read_only}
                      onChange={(e) => setEditData({ ...editData, is_read_only: e.target.checked })}
                    />
                    <label htmlFor="is_read_only" style={{ color: colors.textPrimary, fontSize: '0.9rem' }}>
                      Read-Only
                    </label>
                  </div>
                </div>

                {showAgentPerms && (
                  <div style={{ marginTop: '1.25rem', borderTop: `1px solid ${colors.goldMuted}`, paddingTop: '1rem' }}>
                    {loadingPerms ? (
                      <p style={{ color: colors.textMuted, fontFamily }}>Loading agent permissions...</p>
                    ) : permsError || !perms ? (
                      <p style={{ color: colors.negative, fontFamily }}>{permsError || 'Failed to load agent permissions'}</p>
                    ) : (
                      AGENT_GENERAL_PERMS.map(([field, label, hint]) => permCheckbox(field, label, hint))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={sectionCard}>
            <button onClick={() => toggleSection('limits')} style={sectionHeader}>
              <span>Limits</span>
              {openSections.has('limits') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
            </button>
            {openSections.has('limits') && (
              <div style={{ padding: '0 1.25rem 1.25rem' }}>
                {showAgentPerms ? (
                  loadingPerms ? (
                    <p style={{ color: colors.textMuted, fontFamily }}>Loading agent permissions...</p>
                  ) : permsError || !perms ? (
                    <p style={{ color: colors.negative, fontFamily }}>{permsError || 'Failed to load agent permissions'}</p>
                  ) : (
                    <>
                      <p style={{ ...hintStyle, fontSize: '0.85rem', marginTop: 0, marginBottom: '0.75rem' }}>
                        Enter 0 on any field below for no limit.<br />
                        Range = how far the agent may set this for their own customers, when the box is checked.<br />
                        Default = what a customer gets instead, when it's unchecked.
                      </p>
                      {AGENT_LIMIT_TYPES.map(([key, toggleField, minField, maxField, label, defaultFields, placeholders]) => (
                        <div key={key} style={{ padding: '0.6rem 0', borderBottom: `1px solid ${colors.sectionBg}` }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                            <input type="checkbox" checked={!!perms[toggleField]} onChange={e => setPermBool(toggleField, e.target.checked)} style={{ accentColor: colors.gold, cursor: 'pointer' }} />
                            <span style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
                          </label>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.5rem', marginBottom: '0.35rem' }}>
                            <span style={{ color: colors.textPrimary, fontSize: '0.85rem', fontWeight: 700, width: 58 }}>Range</span>
                            <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                              Min <input type="number" step="0.01" min="0" value={perms[minField]} onChange={e => setPermNum(minField, e.target.value)} style={numInputStyle} />
                            </label>
                            <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                              Max <input type="number" step="0.01" min="0" value={perms[maxField]} onChange={e => setPermNum(maxField, e.target.value)} style={numInputStyle} />
                            </label>
                          </div>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.5rem' }}>
                            <span style={{ color: colors.textPrimary, fontSize: '0.85rem', fontWeight: 700, width: 58 }}>Default</span>
                            {defaultFields.length === 2 ? (
                              <>
                                <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                                  Min <input type="number" step="0.01" min="0" className="hint-placeholder" placeholder={placeholders[0]} value={perms[defaultFields[0]] ?? ''} onChange={e => setPermDefault(defaultFields[0], e.target.value)} style={numInputStyle} />
                                </label>
                                <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                                  Max <input type="number" step="0.01" min="0" className="hint-placeholder" placeholder={placeholders[1]} value={perms[defaultFields[1]] ?? ''} onChange={e => setPermDefault(defaultFields[1], e.target.value)} style={numInputStyle} />
                                </label>
                              </>
                            ) : (
                              <input type="number" step="0.01" min="0" className="hint-placeholder" placeholder={placeholders[0]} value={perms[defaultFields[0]] ?? ''} onChange={e => setPermDefault(defaultFields[0], e.target.value)} style={numInputStyle} />
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )
                ) : (
                  <>
                    <p style={{ ...hintStyle, fontSize: '0.85rem', marginTop: 0, marginBottom: '0.75rem' }}>Enter 0 on any field below for no limit.</p>
                    {limitRow('Weekly Limit ($)',
                      <input type="number" style={limitInputStyle} value={editData.weekly_limit} onChange={(e) => setEditData({ ...editData, weekly_limit: Number(e.target.value) })} />
                    )}
                    {limitRow('Free Play Loss Rebate (%)',
                      <input type="number" min="0" max="100" step="1" style={limitInputStyle} value={editData.free_play_loss_percentage} onChange={(e) => setEditData({ ...editData, free_play_loss_percentage: Number(e.target.value) })} />
                    )}
                    {pairRow('Wager Limit',
                      <input type="number" style={numInputStyle} value={editData.min_bet} onChange={(e) => setEditData({ ...editData, min_bet: Number(e.target.value) })} />,
                      <input type="number" style={numInputStyle} value={editData.max_bet} onChange={(e) => setEditData({ ...editData, max_bet: Number(e.target.value) })} />
                    )}
                    {pairRow('Live Wager Limit',
                      <input type="number" step="0.01" min="0.01" style={numInputStyle} value={editData.live_min_bet} onChange={(e) => setEditData({ ...editData, live_min_bet: Number(e.target.value) })} />,
                      <input type="number" step="0.01" min="0" style={numInputStyle} value={editData.live_max_bet} onChange={(e) => setEditData({ ...editData, live_max_bet: Number(e.target.value) })} />
                    )}
                    {pairRow('Parlay Limit',
                      <input type="number" step="0.01" min="0" style={numInputStyle} value={editData.parlay_min_bet} onChange={(e) => setEditData({ ...editData, parlay_min_bet: Number(e.target.value) })} />,
                      <input type="number" step="0.01" min="0" style={numInputStyle} value={editData.parlay_max_bet} onChange={(e) => setEditData({ ...editData, parlay_max_bet: Number(e.target.value) })} />
                    )}
                    {limitRow('Parlay Max Payout ($)',
                      <input type="number" step="0.01" min="0" style={limitInputStyle} value={editData.parlay_max_payout} onChange={(e) => setEditData({ ...editData, parlay_max_payout: Number(e.target.value) })} />
                    )}
                    {limitRow('Contest Limit ($)',
                      <input type="number" step="0.01" min="0" style={limitInputStyle} value={editData.game_limit} onChange={(e) => setEditData({ ...editData, game_limit: Number(e.target.value) })} />
                    )}
                    {pairRow('Casino Limit',
                      <input type="number" step="0.01" min="0.01" style={numInputStyle} value={editData.casino_min_bet} onChange={(e) => setEditData({ ...editData, casino_min_bet: Number(e.target.value) })} />,
                      <input type="number" step="0.01" min="0" style={numInputStyle} value={editData.casino_max_bet} onChange={(e) => setEditData({ ...editData, casino_max_bet: Number(e.target.value) })} />
                    )}
                    {limitRow('Casino Weekly Max Win ($)',
                      <input type="number" step="0.01" min="0" style={limitInputStyle} value={editData.casino_weekly_max_win} onChange={(e) => setEditData({ ...editData, casino_weekly_max_win: Number(e.target.value) })} />
                    )}
                    {limitRow('Casino Weekly Max Loss ($)',
                      <input type="number" step="0.01" min="0" style={limitInputStyle} value={editData.casino_weekly_max_loss} onChange={(e) => setEditData({ ...editData, casino_weekly_max_loss: Number(e.target.value) })} />
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {showAgentPerms && (
            <div style={sectionCard}>
              <button onClick={() => toggleSection('siteOptions')} style={sectionHeader}>
                <span>Site Options</span>
                {openSections.has('siteOptions') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
              </button>
              {openSections.has('siteOptions') && (
                <div style={{ padding: '0 1.25rem 1.25rem' }}>
                  {loadingPerms ? (
                    <p style={{ color: colors.textMuted, fontFamily }}>Loading agent permissions...</p>
                  ) : permsError || !perms ? (
                    <p style={{ color: colors.negative, fontFamily }}>{permsError || 'Failed to load agent permissions'}</p>
                  ) : (
                    AGENT_SITE_OPTIONS.map(([field, label, hint]) => permCheckbox(field, label, hint))
                  )}
                </div>
              )}
            </div>
          )}

          {selectedUser.role === 'customer' && (currentUser.role === 'book' || currentUser.role === 'agent') && (
            <div style={sectionCard}>
              <button onClick={() => toggleSection('balance')} style={sectionHeader}>
                <span>Balance Adjustment</span>
                {openSections.has('balance') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
              </button>
              {openSections.has('balance') && (
                <div style={{ padding: '0 1.25rem 1.25rem' }}>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    Current Balance: <span style={{ fontWeight: 700, color: (selectedUser.weekly_balance ?? 0) >= 0 ? colors.odds : colors.negative }}>{(selectedUser.weekly_balance ?? 0).toFixed(2)}<CC /></span>
                  </p>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    Add or subtract from this customer's balance.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Amount ($)</label>
                      <input type="number" step="0.01" placeholder="e.g., 50 or -25" style={inputStyle} value={balanceAdjustment} onChange={(e) => setBalanceAdjustment(e.target.value)} />
                    </div>
                    <div>
                      <label style={labelStyle}>Reason (optional)</label>
                      <input placeholder="e.g., Weekly bonus" style={inputStyle} value={adjustmentReason} onChange={(e) => setAdjustmentReason(e.target.value)} />
                    </div>
                  </div>
                  {balanceAdjustment && parseFloat(balanceAdjustment) !== 0 && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', fontWeight: 600, color: parseFloat(balanceAdjustment) > 0 ? colors.odds : colors.negative }}>
                      Will {parseFloat(balanceAdjustment) > 0 ? 'credit' : 'debit'} {Math.abs(parseFloat(balanceAdjustment)).toFixed(2)}<CC /> {parseFloat(balanceAdjustment) > 0 ? 'to' : 'from'} account
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {selectedUser.role === 'customer' && (currentUser.role === 'book' || currentUser.role === 'agent') && (
            <div style={sectionCard}>
              <button onClick={() => toggleSection('freeplay')} style={sectionHeader}>
                <span>Free Play Balance</span>
                {openSections.has('freeplay') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
              </button>
              {openSections.has('freeplay') && (
                <div style={{ padding: '0 1.25rem 1.25rem' }}>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Current Balance: <span style={{ fontWeight: 700, color: colors.gold }}>{(selectedUser.free_play_balance || 0).toFixed(2)}<CC /></span>
                  </p>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    Add or subtract from this customer's free play.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Amount ($)</label>
                      <input type="number" step="0.01" placeholder="e.g., 25 or -10" style={inputStyle} value={freePlayAdjustment} onChange={(e) => setFreePlayAdjustment(e.target.value)} />
                    </div>
                    <div>
                      <label style={labelStyle}>Reason (optional)</label>
                      <input placeholder="e.g., Sign-up bonus" style={inputStyle} value={freePlayReason} onChange={(e) => setFreePlayReason(e.target.value)} />
                    </div>
                  </div>
                  {freePlayAdjustment && parseFloat(freePlayAdjustment) !== 0 && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', fontWeight: 600, color: parseFloat(freePlayAdjustment) > 0 ? colors.odds : colors.negative }}>
                      Will {parseFloat(freePlayAdjustment) > 0 ? 'add' : 'subtract'} {Math.abs(parseFloat(freePlayAdjustment)).toFixed(2)}<CC /> {parseFloat(freePlayAdjustment) > 0 ? 'to' : 'from'} free play balance
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {selectedUser.role === 'customer' && (
            <div style={sectionCard}>
              <button onClick={() => toggleSection('pending')} style={sectionHeader}>
                <span>Pending Bets</span>
                {openSections.has('pending') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
              </button>
              {openSections.has('pending') && (
                <div style={{ padding: '0 1.25rem 1.25rem' }}>
                  {loadingBets ? (
                    <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Loading...</p>
                  ) : pendingBets.length === 0 ? (
                    <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>No pending bets.</p>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      {pendingBets.map((bet) => {
                        const rowKey = `${bet.is_parlay ? 'p' : 'b'}-${bet.id}`;
                        return (
                        <div key={rowKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: colors.cardBg, borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                          <div style={{ color: colors.textPrimary, fontSize: '0.85rem' }}>
                            {bet.is_parlay && (
                              <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 10, background: 'rgba(168,85,247,0.2)', color: colors.freePlay, marginRight: 6 }}>
                                {bet.num_legs}-Leg Parlay
                              </span>
                            )}
                            <span style={{ fontWeight: 600 }}>{bet.description}</span>
                            {' — '}${bet.amount.toFixed(2)}{bet.odds ? ` (${bet.odds})` : ''}
                          </div>
                          <button
                            onClick={() => handleVoidBet(bet)}
                            disabled={voidingBetId === rowKey}
                            style={{ background: 'rgba(239,68,68,0.2)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 6, padding: '0.3rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer' }}
                          >
                            {voidingBetId === rowKey ? 'Voiding...' : 'Void'}
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <button onClick={handleUpdate} disabled={saving || (showAgentPerms && loadingPerms)} style={saveButtonStyle}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
